import React, { useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    ButtonGroup,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    InputAdornment,
    FormControl,
    Grid,
    FormLabel,
    FormControlLabel,
    Checkbox,
    Box,
} from "@mui/material";
import {
    Add,
    Delete,
    Save,
    ExpandMore,
    Cancel,
    ArrowUpward,
    ArrowDownward,
} from "@mui/icons-material";
import ConfirmDeleteDialog from "../../ConfirmDeleteDialog";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, FieldArray } from "formik";
import { TextField, Select } from "formik-mui";
import {
    createOrUpdateProjectSubThemes,
    deleteProjectSubTheme,
} from "../ProjectActions";
import { global, importanceLevels, scoreLevels } from "../../../context/global";
import {
    goals2,
    goals as goalsText,
} from "../../../context/preparationPhase.js";
import InformationDialogue from "../../CustomComponents/InformationDialogue";
import StepperButtons from "../StepperButtons.js";
import { toast } from "react-toastify";
import CustomNumberfield from "../../CustomComponents/InputFields/CustomNumberfield.js";
import * as Yup from "yup";

// Helper component for rendering goal value input (select or number field)
function GoalValueInput({
    goal,
    subthemeIndex,
    goalIndex,
    values,
    errors,
    editable,
}) {
    const { flatKpis } = useSelector((state) => state.themes);
    const kpiId = values.subthemes[subthemeIndex].goals[goalIndex].kpi;
    const kpi = flatKpis[kpiId];
    const fieldName = `subthemes.${subthemeIndex}.goals.${goalIndex}.value`;
    const errorPath =
        errors.subthemes?.[subthemeIndex]?.goals?.[goalIndex]?.value;

    if (!goal.kpi) return null;

    if (kpi.scale_type === 0) {
        return (
            <Grid item xs={5} sx={{ width: 200 }}>
                <Field
                    size="small"
                    component={Select}
                    name={fieldName}
                    inputProps={{ id: fieldName }}
                    sx={{ width: 208 }}
                >
                    {scoreLevels.map((option) => (
                        <MenuItem key={option.label} value={option.value}>
                            {option.value}. {option.label}
                        </MenuItem>
                    ))}
                </Field>
            </Grid>
        );
    }

    return (
        <Grid item xs={5}>
            <Field
                size="small"
                component={CustomNumberfield}
                name={fieldName}
                id={fieldName}
                unit={kpi.unit}
                startAdornment={
                    <InputAdornment position="start">
                        {kpi.maximize ? "≥" : "≤"}
                    </InputAdornment>
                }
                disabled={!editable}
                fullWidth={false}
                error={errorPath}
                helperText={errorPath}
            />
        </Grid>
    );
}

// Helper component for rendering a single goal row
function GoalRow({
    goal,
    goalIndex,
    subthemeIndex,
    projectsubtheme,
    values,
    errors,
    editable,
    isSubmitting,
    setFieldValue,
    selectedKpisInGoal,
    setSelectedKpisInGoal,
    arrayHelpers,
}) {
    const { flatSubThemes, flatKpis } = useSelector((state) => state.themes);
    const addValueToObject = (subtheme, gIndex, value) => {
        setSelectedKpisInGoal({
            ...selectedKpisInGoal,
            [subtheme]: selectedKpisInGoal[subtheme]
                ? { ...selectedKpisInGoal[subtheme], [gIndex]: value }
                : { [gIndex]: value },
        });
    };

    const isValid = (subtheme, gIndex, value) => {
        let valid = true;
        for (let key in selectedKpisInGoal[subtheme]) {
            const keyNum = Number(key);
            const gIndexNum = Number(gIndex);
            if (
                keyNum !== gIndexNum &&
                selectedKpisInGoal[subtheme][key] === value
            ) {
                toast.error("Dubbele KPI's zijn niet toegestaan");
                valid = false;
            } else if (
                keyNum === gIndexNum &&
                selectedKpisInGoal[subtheme][gIndex] !== value
            ) {
                addValueToObject(subtheme, gIndex, value);
            }
        }
        return valid;
    };

    const chosenKpiIDs = projectsubtheme.goals.map((g) => g.kpi);
    const subthemeKpis = flatSubThemes[projectsubtheme.subtheme]?.kpis || [];
    const availableKpis = subthemeKpis.filter(
        (kpi) =>
            !chosenKpiIDs.includes(kpi.id) &&
            !chosenKpiIDs.includes(kpi.id.toString())
    );

    const kpiFieldName = `subthemes.${subthemeIndex}.goals.${goalIndex}.kpi`;
    const currentKpi = flatKpis[projectsubtheme.goals[goalIndex].kpi];
    const kpiOptions = [currentKpi, ...availableKpis];

    const handleKpiBlur = (event) => {
        const value = Number(event.target.value);
        if (!isValid(subthemeIndex, goalIndex, value)) {
            setFieldValue(kpiFieldName, "");
        } else {
            addValueToObject(subthemeIndex, goalIndex, value);
        }
    };

    return (
        <>
            <Grid item xs={goal.kpi ? 6 : 11} alignItems="end">
                <Field
                    size="small"
                    component={Select}
                    name={kpiFieldName}
                    inputProps={{ id: kpiFieldName }}
                    sx={{ width: 250 }}
                    disabled={!editable}
                    onBlur={handleKpiBlur}
                >
                    {kpiOptions.map((kpi) => (
                        <MenuItem key={kpi?.id} value={kpi?.id}>
                            {kpi?.name}{" "}
                            {kpi?.unit !== undefined
                                ? `(${kpi?.unit || "punten"})`
                                : null}
                        </MenuItem>
                    ))}
                </Field>
            </Grid>
            <GoalValueInput
                goal={goal}
                subthemeIndex={subthemeIndex}
                goalIndex={goalIndex}
                flatKpis={flatKpis}
                values={values}
                errors={errors}
                editable={editable}
            />
            <Grid item xs={1}>
                <IconButton
                    onClick={() => arrayHelpers.remove(goalIndex)}
                    disabled={!editable || isSubmitting}
                >
                    <Cancel />
                </IconButton>
            </Grid>
        </>
    );
}

// Helper component for goals field array
function GoalsFieldArray({
    subthemeIndex,
    projectsubtheme,
    values,
    errors,
    editable,
    isSubmitting,
    setFieldValue,
    selectedKpisInGoal,
    setSelectedKpisInGoal,
    newProjectGoal,
}) {
    const { flatSubThemes } = useSelector((state) => state.themes);
    const fieldName = `subthemes.${subthemeIndex}.goals`;
    const maxGoals = flatSubThemes[projectsubtheme.subtheme]?.kpis?.length || 0;

    return (
        <FieldArray
            name={fieldName}
            render={(arrayHelpers) => (
                <>
                    {projectsubtheme.goals.map((goal, goalIndex) => (
                        <GoalRow
                            key={goalIndex}
                            goal={goal}
                            goalIndex={goalIndex}
                            subthemeIndex={subthemeIndex}
                            projectsubtheme={projectsubtheme}
                            values={values}
                            errors={errors}
                            editable={editable}
                            isSubmitting={isSubmitting}
                            setFieldValue={setFieldValue}
                            selectedKpisInGoal={selectedKpisInGoal}
                            setSelectedKpisInGoal={setSelectedKpisInGoal}
                            arrayHelpers={arrayHelpers}
                        />
                    ))}
                    {projectsubtheme.goals.length < maxGoals && (
                        <Grid item xs={12}>
                            <Button
                                startIcon={<Add />}
                                onClick={() =>
                                    arrayHelpers.push(
                                        newProjectGoal(projectsubtheme)
                                    )
                                }
                                disabled={!editable || isSubmitting}
                            >
                                {global.add}
                            </Button>
                        </Grid>
                    )}
                </>
            )}
        />
    );
}

// Helper component for subtheme row
function SubthemeRow({
    projectsubtheme,
    subthemeIndex,
    availableSubthemes,
    values,
    errors,
    editable,
    isSubmitting,
    setFieldValue,
    selectedKpisInGoal,
    setSelectedKpisInGoal,
    newProjectGoal,
    handleClickDeleteSubtheme,
    arrayHelpers,
    payload,
}) {
    const { flatSubThemes } = useSelector((state) => state.themes);
    const subthemeFieldName = `subthemes.${subthemeIndex}.subtheme`;
    const descFieldName = `subthemes.${subthemeIndex}.description`;
    const currentSubtheme = flatSubThemes[projectsubtheme.subtheme];
    const subthemeOptions = [currentSubtheme, ...availableSubthemes];

    return (
        <TableRow
            sx={{
                verticalAlign: "top",
                borderTop: "2px solid",
                borderBottom: "2px solid",
                borderColor: "primary.main",
                padding: 2,
            }}
        >
            <TableCell padding="none">
                <Field
                    size="small"
                    component={Select}
                    name={subthemeFieldName}
                    inputProps={{ id: subthemeFieldName }}
                    // autoWidth
                    sx={{ minWidth: 200, display: "flex" }}
                    disabled={!editable}
                    // fullWidth={true}
                >
                    {subthemeOptions.map((subtheme) => (
                        <MenuItem key={subtheme?.id} value={subtheme?.id}>
                            {subtheme?.name}
                        </MenuItem>
                    ))}
                </Field>
            </TableCell>
            <TableCell sx={{ minWidth: 400 }}>
                <Field
                    component={TextField}
                    name={descFieldName}
                    inputProps={{ id: descFieldName }}
                    multiline
                    maxRows={6}
                    fullWidth
                    disabled={!editable}
                />
            </TableCell>
            <TableCell sx={{ minWidth: 500 }}>
                <Grid container spacing={0} justifyContent="end">
                    <GoalsFieldArray
                        subthemeIndex={subthemeIndex}
                        projectsubtheme={projectsubtheme}
                        values={values}
                        errors={errors}
                        editable={editable}
                        isSubmitting={isSubmitting}
                        setFieldValue={setFieldValue}
                        selectedKpisInGoal={selectedKpisInGoal}
                        setSelectedKpisInGoal={setSelectedKpisInGoal}
                        newProjectGoal={newProjectGoal}
                        payload={payload}
                    />
                </Grid>
            </TableCell>
            <TableCell width="5%">
                <ButtonGroup>
                    <IconButton
                        onClick={() => {
                            handleClickDeleteSubtheme(projectsubtheme);
                            arrayHelpers.remove(subthemeIndex);
                        }}
                        disabled={isSubmitting || !editable}
                    >
                        <Tooltip
                            placement="right-end"
                            title="Aspect verwijderen"
                        >
                            <Delete />
                        </Tooltip>
                    </IconButton>
                </ButtonGroup>
            </TableCell>
        </TableRow>
    );
}

// Helper component for form action buttons
function FormActionButtons({
    values,
    projecttheme,
    projectThemeIndex,
    filteredProjectThemesLength,
    editable,
    isSubmitting,
    isValid,
    handleSubmit,
    setFieldValue,
    setExpanded,
    newProjectSubTheme,
}) {
    const { flatThemes } = useSelector((state) => state.themes);
    const themeSubthemes = flatThemes[projecttheme.theme]?.subthemes || [];
    const canAddSubtheme = values.subthemes.length !== themeSubthemes.length;

    return (
        <ButtonGroup variant="contained" color="secondary">
            {canAddSubtheme && (
                <Button
                    startIcon={<Add />}
                    onClick={() => {
                        setFieldValue("subthemes", [
                            ...values.subthemes,
                            newProjectSubTheme(projecttheme),
                        ]);
                    }}
                    disabled={isSubmitting || !editable}
                >
                    {global.subtheme} {global.add}
                </Button>
            )}
            {editable && (
                <Button
                    type="submit"
                    startIcon={<Save />}
                    disabled={isSubmitting}
                />
            )}
            {projectThemeIndex > 0 && (
                <Button
                    onClick={() => {
                        if (editable) handleSubmit();
                        setExpanded(Math.max(projectThemeIndex - 1, 0));
                    }}
                    startIcon={<ArrowUpward />}
                    disabled={isSubmitting || !isValid}
                >
                    {editable ? `${global.save} ${global.and} ` : ""}{" "}
                    {global.previous} {global.theme}
                </Button>
            )}
            {projectThemeIndex < filteredProjectThemesLength - 1 && (
                <Button
                    onClick={() => {
                        if (editable) handleSubmit();
                        setExpanded(
                            Math.min(
                                projectThemeIndex + 1,
                                filteredProjectThemesLength - 1
                            )
                        );
                    }}
                    endIcon={<ArrowDownward />}
                    disabled={isSubmitting || !isValid}
                >
                    {editable && `${global.save} ${global.and} `}{" "}
                    {`${global.next} ${global.theme}`}
                </Button>
            )}
        </ButtonGroup>
    );
}

export default function ProjectGoals({ payload }) {
    const dispatch = useDispatch();
    const { flatThemes } = useSelector((state) => state.themes);
    const projectThemes = payload.project.themes;
    const [minimumImportanceFilter, setMinimumImportanceFilter] = useState([
        1, 2, 3,
    ]);
    const filteredProjectThemes = projectThemes.filter((theme) =>
        minimumImportanceFilter.includes(theme.importance)
    );
    const [subthemeToDelete, setSubthemeToDelete] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedKpisInGoal, setSelectedKpisInGoal] = useState({});

    payload.editable =
        payload.editable &&
        (payload.project.status == 0 || payload.project.status == 3);

    const [expanded, setExpanded] = useState(0);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleCreateOrUpdateProjectSubThemes = (subthemes, setSubmitting) => {
        if (subthemes.length > 0) {
            createOrUpdateProjectSubThemes(subthemes, dispatch);
        }
        setSubmitting(false);
    };

    const handleClickDeleteSubtheme = (subtheme) => {
        setSubthemeToDelete(subtheme);
        setDeleteDialogOpen(true);
    };

    const newProjectSubTheme = (projectTheme) => {
        const subThemes = flatThemes[projectTheme.theme]?.subthemes;
        return {
            project_theme: projectTheme.id,
            importance: projectTheme.importance,
            description: "",
            subtheme:
                Object.values(subThemes).length === 1
                    ? Object.values(subThemes)[0].id
                    : "",
            goals: [],
        };
    };

    const newProjectGoal = (projectSubtheme) => ({
        project_subtheme: projectSubtheme.id,
        kpi: "",
        description: "",
        value: "",
    });

    if (payload.project.status < 2) {
        return (
            <Typography>
                Vergrendel de thema's om doelen toe te kunnen voegen.
            </Typography>
        );
    }
    const validationSchema = Yup.object({
        subthemes: Yup.array().of(
            Yup.object().shape({
                subtheme: Yup.number().required("Aspect is verplicht"),
                goals: Yup.array().of(
                    Yup.object().shape({
                        kpi: Yup.number().required("KPI is verplicht"),
                        value: Yup.string().required("Waarde is verplicht"),
                    })
                ),
            })
        ),
        isValid: Yup.boolean().required(),
    });

    const handleFormSubmit = (values, { setSubmitting }) => {
        if (values.isValid) {
            handleCreateOrUpdateProjectSubThemes(
                values.subthemes,
                setSubmitting
            );
        } else {
            toast.error("Er zijn ongeldige waarden ingevuld");
            setSubmitting(false);
        }
    };

    const handleImportanceChange = (level, checked) => {
        if (checked) {
            setMinimumImportanceFilter([
                ...minimumImportanceFilter,
                level.value,
            ]);
        } else {
            setMinimumImportanceFilter(
                minimumImportanceFilter.filter((val) => val !== level.value)
            );
        }
    };

    const handleDeleteConfirm = () => {
        deleteProjectSubTheme(
            subthemeToDelete?.id,
            subthemeToDelete?.project_theme,
            dispatch
        );
        setDeleteDialogOpen(false);
    };

    const renderImportanceFilter = () => (
        <FormControl>
            <FormLabel>Inspanningsniveau</FormLabel>
            <Stack direction="row" spacing={1}>
                {importanceLevels.map((level) => (
                    <FormControlLabel
                        key={level.value}
                        control={
                            <Checkbox
                                checked={minimumImportanceFilter.includes(
                                    level.value
                                )}
                                onChange={(e) =>
                                    handleImportanceChange(
                                        level,
                                        e.target.checked
                                    )
                                }
                            />
                        }
                        label={level.label}
                    />
                ))}
            </Stack>
        </FormControl>
    );

    const renderSubthemesTable = (
        values,
        errors,
        isSubmitting,
        setFieldValue,
        projecttheme,
        arrayHelpers
    ) => {
        const chosenSubthemeIDs = values.subthemes.map(
            (projectsubtheme) => projectsubtheme.subtheme
        );
        const themeSubthemes = flatThemes[projecttheme.theme]?.subthemes || [];
        const availableSubthemes = themeSubthemes.filter(
            (subtheme) =>
                !chosenSubthemeIDs.includes(subtheme.id) &&
                !chosenSubthemeIDs.includes(subtheme.id.toString())
        );

        return (
            <>
                {values.subthemes.map((projectsubtheme, subthemeIndex) => (
                    <SubthemeRow
                        key={subthemeIndex}
                        projectsubtheme={projectsubtheme}
                        subthemeIndex={subthemeIndex}
                        availableSubthemes={availableSubthemes}
                        values={values}
                        errors={errors}
                        editable={payload.editable}
                        isSubmitting={isSubmitting}
                        setFieldValue={setFieldValue}
                        selectedKpisInGoal={selectedKpisInGoal}
                        setSelectedKpisInGoal={setSelectedKpisInGoal}
                        newProjectGoal={newProjectGoal}
                        handleClickDeleteSubtheme={handleClickDeleteSubtheme}
                        arrayHelpers={arrayHelpers}
                        payload={payload}
                    />
                ))}
            </>
        );
    };

    const renderThemeForm = (projecttheme, projectThemeIndex) => (
        <Formik
            enableReinitialize
            initialValues={{
                subthemes: projecttheme.subthemes || [],
                isValid: true,
            }}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
        >
            {({
                values,
                isSubmitting,
                setFieldValue,
                handleSubmit,
                isValid,
                errors,
            }) => (
                <Form>
                    {values.subthemes?.length > 0 ? (
                        <TableContainer>
                            <Table size="small" padding="none">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox">
                                            Aspect
                                        </TableCell>
                                        <TableCell>Toelichting</TableCell>
                                        <TableCell>Doelen</TableCell>
                                        <TableCell>Acties</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <FieldArray
                                        name="subthemes"
                                        render={(arrayHelpers) =>
                                            renderSubthemesTable(
                                                values,
                                                errors,
                                                isSubmitting,
                                                setFieldValue,
                                                projecttheme,
                                                arrayHelpers
                                            )
                                        }
                                    />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography>
                            {flatThemes[projecttheme.theme]?.subthemes
                                ?.length === 0
                                ? "Er zijn geen aspecten of KPI's voor dit thema."
                                : ""}
                        </Typography>
                    )}
                    <Box display="flex" sx={{ mt: 2 }}>
                        <FormActionButtons
                            values={values}
                            projecttheme={projecttheme}
                            projectThemeIndex={projectThemeIndex}
                            filteredProjectThemesLength={
                                filteredProjectThemes.length
                            }
                            editable={payload.editable}
                            isSubmitting={isSubmitting}
                            isValid={isValid}
                            handleSubmit={handleSubmit}
                            setFieldValue={setFieldValue}
                            setExpanded={setExpanded}
                            newProjectSubTheme={newProjectSubTheme}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );

    const renderThemeAccordion = (projecttheme, projectThemeIndex) => (
        <Accordion
            key={projecttheme.id}
            expanded={expanded === projectThemeIndex}
            onChange={handleChange(projectThemeIndex)}
        >
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack>
                    <Typography variant="h6">
                        {flatThemes[projecttheme.theme]?.name}
                    </Typography>
                    <Typography>
                        Niveau:{" "}
                        {importanceLevels[projecttheme.importance].label}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Typography>{projecttheme.description}</Typography>
                {renderThemeForm(projecttheme, projectThemeIndex)}
            </AccordionDetails>
        </Accordion>
    );

    const renderThemesList = () => {
        if (filteredProjectThemes.length > 0) {
            return filteredProjectThemes.map(renderThemeAccordion);
        }

        const message =
            projectThemes.length === 0
                ? "Er zijn nog geen thema's toegevoegd aan dit project."
                : "Er zijn geen thema's van het geselecteerde niveau.";

        return <Typography>{message}</Typography>;
    };

    return (
        <>
            <ConfirmDeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                handleConfirm={handleDeleteConfirm}
                dialogTitle="Aspect verwijderen?"
                dialogContentText="Weet u zeker dat u dit aspect wilt verwijderen?"
            />
            <Stack spacing={1}>
                <InformationDialogue text={goalsText} />
                <Typography fontStyle="italic">{goals2}</Typography>
                {projectThemes.length > 0 && renderImportanceFilter()}
                {renderThemesList()}
                <StepperButtons
                    payload={payload}
                    updateStatus={false}
                    warningPopup={payload.project.status <= 3}
                />
            </Stack>
        </>
    );
}
